import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { CreditCard, RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

export default function BillingView() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filterIndustry, setFilterIndustry] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/enhance/billing/plans');
        setPlans(data.plans || []);
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, []);

  const handleCheckout = async (planId) => {
    setProcessing(planId);
    try {
      const { data } = await api.post('/api/enhance/billing/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch { toast.error('Checkout failed'); }
    finally { setProcessing(null); }
  };

  const industries = [...new Set(plans.map(p => p.industry))];
  const filtered = filterIndustry ? plans.filter(p => p.industry === filterIndustry) : plans;

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#D4A843]" /> Billing Plans
          </h1>
          <p className="text-sm text-gray-500 mt-1">Usage-based billing per industry vertical</p>
        </div>
      </div>

      {/* Industry Filter */}
      <div className="flex items-center gap-2">
        <button onClick={() => setFilterIndustry('')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${!filterIndustry ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="billing-filter-all">All</button>
        {industries.map(ind => (
          <button key={ind} onClick={() => setFilterIndustry(ind)} className={`px-3 py-1.5 rounded text-xs font-medium capitalize transition-colors ${filterIndustry === ind ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`billing-filter-${ind}`}>
            {ind}
          </button>
        ))}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="billing-plans-grid">
        {filtered.map(plan => (
          <div key={plan.id} className={`bg-white border rounded-lg p-6 hover-lift ${plan.price >= 400 ? 'border-[#D4A843] ring-1 ring-[#D4A843]/10' : 'border-gray-200'}`} data-testid={`billing-plan-${plan.id}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded">{plan.industry}</span>
              {plan.price >= 400 && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded">Popular</span>}
            </div>
            <h3 className="text-sm font-bold text-gray-900 mb-1">{plan.name}</h3>
            <p className="text-3xl font-bold text-gray-900 mb-4">${plan.price}<span className="text-sm text-gray-400 font-normal">/mo</span></p>
            <ul className="space-y-2 mb-5">
              {plan.features.map((f, i) => (
                <li key={`item-${i}`} className="flex items-center gap-2 text-xs text-gray-600">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button className="w-full bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => handleCheckout(plan.id)} disabled={processing === plan.id} data-testid={`billing-checkout-${plan.id}`}>
              {processing === plan.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />} Subscribe
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
